/**
 * 4-blok — «Elektron yashil olam»: darhol sotuvga qo'yilgan mahsulotlar.
 * Tur bo'yicha saralash, rasm, nom, narx, sotuvchi va hudud. Ko'rish uchun
 * kirish shart emas; «Sotib olish» OneID orqali kirishni so'raydi.
 * «Barcha mahsulotlar» — to'liq savdo sahifasi (`/yashil-olam`).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useI18n } from "@/shared/i18n";
import { useReveal } from "@/shared/lib/motion";
import { MARKET_ANCHOR, SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { Icon } from "@/shared/ui/Icon";

import { MarketCard, TypeFilter, useBuy, useCategoryOptions, useMarketCatalog, useTypeOptions } from "./market";

/** Bosh sahifada bitta qator (to'liq katalog — «Elektron yashil olam» sahifasida). */
const HOME_COUNT = 4;

/** Fisher–Yates: asl ro'yxatni o'zgartirmay, aralashtirilgan nusxa qaytaradi. */
function shuffle<T>(list: T[]): T[] {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function MarketSection() {
  const { t } = useI18n();
  const catalog = useMarketCatalog(100);
  const buy = useBuy();
  const options = useTypeOptions(catalog.items);
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const categories = useCategoryOptions(catalog.items, type);

  /* Vitrina har yuklanganda tasodifiy mahsulotlarni ko'rsatadi. */
  const shuffled = useMemo(() => shuffle(catalog.items), [catalog.items]);
  const visible = shuffled.filter((item) => (!type || item.typeCode === type) && (!category || item.categoryCode === category)).slice(0, HOME_COUNT);

  /* Kartochkalar katalog kelgach va tur almashganda chiziladi — ko'rinish animatsiyasi ularni ham qamrasin. */
  useReveal([catalog.loading, catalog.items.length, type, category]);

  return (
    <section className="section section-alt home-market" id={MARKET_ANCHOR} aria-labelledby="market-title">
      <div className="container">
        <div className="home-head" data-reveal>
          <div>
            <span className="eyebrow">{t("nav.market")}</span>
            <h2 id="market-title">{t("market.title")}</h2>
            <p className="section-lead">{t("market.lead")}</p>
          </div>
          <Link className="btn btn-outline" to={SECTION_PATHS.market}>
            {t("market.all")}
            <Icon name="arrowRight" size={16} />
          </Link>
        </div>

        <div data-reveal>
          <TypeFilter
            options={options}
            value={type}
            onChange={(code) => {
              setType(code);
              setCategory("");
            }}
          />
        </div>
        {categories.length ? (
          <div className="market-subfilter" data-reveal>
            <TypeFilter options={categories} value={category} onChange={setCategory} />
          </div>
        ) : null}

        {catalog.sample ? (
          <p className="sample-note">
            <Icon name="info" size={14} />
            {t("market.sampleNote")}
          </p>
        ) : null}

        {visible.length ? (
          <div className="market-grid is-one-row">
            {visible.map((item, index) => (
              <MarketCard key={item.key} item={item} index={index} onBuy={buy} />
            ))}
          </div>
        ) : (
          <p className="market-empty">{catalog.loading ? t("state.loading") : t("market.empty")}</p>
        )}

        <p className="market-hint">
          <Icon name="lock" size={14} />
          {t("market.buyHint")}
        </p>
      </div>
    </section>
  );
}
